declare const BACKEND_URL: string;

export type OAuthProvider = 'google' | 'google-workspace' | 'apple';

export const getOAuthLoginUrl = (provider: OAuthProvider): string => `${BACKEND_URL}/api/auth/${provider}`;

// ─── Auth ────────────────────────────────────────────────────────────────────

export const login = async (username: string, password: string): Promise<string> => {
  const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
    method: 'POST',
    headers: {
      'X-Internal-Authorization': `Bearer ${btoa(`${username}:${password}`)}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error('Login failed');
  const { authToken } = await response.json();
  return authToken;
};

export const verifyToken = async (token: string): Promise<boolean> => {
  const response = await fetch(`${BACKEND_URL}/api/auth/verify-token`, {
    method: 'POST',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) return false;
  const { valid } = await response.json();
  return valid;
};

// ─── Types ────────────────────────────────────────────────────────────────────

export type BuilderMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  duration: number;
};

export type BuilderSession = {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
};

export type BuilderSessionDetail = BuilderSession & {
  html_content: string | null;
  messages: BuilderMessage[];
};

export type BuilderCompletionResponse = {
  status: 'OK' | 'Error';
  htmlContent: string | null;
  userMessage: BuilderMessage;
  assistantMessage: BuilderMessage;
  errorMessage?: string;
};

// ─── Sessions API (/api/flex-builder/sessions) ────────────────────────────────

const authHeader = (token: string) => ({
  'X-Internal-Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json',
});

export const fetchSessions = async (token: string): Promise<BuilderSession[]> => {
  const res = await fetch(`${BACKEND_URL}/api/flex-builder/sessions`, {
    headers: authHeader(token),
  });
  if (!res.ok) throw new Error('Failed to fetch sessions');
  const data = await res.json();
  return data.sessions as BuilderSession[];
};

export const createSession = async (token: string, name: string): Promise<BuilderSession> => {
  const res = await fetch(`${BACKEND_URL}/api/flex-builder/sessions`, {
    method: 'POST',
    headers: authHeader(token),
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error('Failed to create session');
  const data = await res.json();
  return data.session as BuilderSession;
};

export const fetchSession = async (token: string, sessionId: string): Promise<BuilderSessionDetail> => {
  const res = await fetch(
    `${BACKEND_URL}/api/flex-builder/sessions/${encodeURIComponent(sessionId)}`,
    { headers: authHeader(token) }
  );
  if (!res.ok) throw new Error('Failed to fetch session');
  const data = await res.json();
  return data.session as BuilderSessionDetail;
};

export const renameSession = async (token: string, sessionId: string, name: string): Promise<void> => {
  const res = await fetch(
    `${BACKEND_URL}/api/flex-builder/sessions/${encodeURIComponent(sessionId)}`,
    { method: 'PATCH', headers: authHeader(token), body: JSON.stringify({ name }) }
  );
  if (!res.ok) throw new Error('Failed to rename session');
};

export const deleteSession = async (token: string, sessionId: string): Promise<void> => {
  const res = await fetch(
    `${BACKEND_URL}/api/flex-builder/sessions/${encodeURIComponent(sessionId)}`,
    { method: 'DELETE', headers: authHeader(token) }
  );
  if (!res.ok) throw new Error('Failed to delete session');
};

// ─── Completion (/api/flex-builder/completion) ────────────────────────────────

export const sendBuilderMessage = async (
  token: string,
  sessionId: string,
  message: string
): Promise<BuilderCompletionResponse> => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/flex-builder/completion`, {
      method: 'POST',
      headers: authHeader(token),
      body: JSON.stringify({ sessionId, message }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const msg = err.message || `Request failed (${res.status})`;
      return errorResponse(msg);
    }

    return await res.json() as BuilderCompletionResponse;
  } catch (e) {
    return errorResponse(e instanceof Error ? e.message : 'Network error');
  }
};

function errorResponse(msg: string): BuilderCompletionResponse {
  return {
    status: 'Error',
    htmlContent: null,
    userMessage: { id: '', role: 'user', content: '', timestamp: new Date().toISOString(), duration: 0 },
    assistantMessage: { id: `err-${Date.now()}`, role: 'assistant', content: msg, timestamp: new Date().toISOString(), duration: 0 },
    errorMessage: msg,
  };
}
